import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { components } from '@/api/types';

type UserInfo = components['schemas']['User'];

// Cookie键名常量
const COOKIE_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  userId: 'userId',
} as const;

// 安全的获取cookie方法
const getCookie = (key: keyof typeof COOKIE_KEYS): string | null => {
  try {
    const value = Cookies.get(COOKIE_KEYS[key]);
    // 更严格的值检查
    if (!value || value === 'undefined' || value === 'null' || value.trim() === '') {
      return null;
    }
    return value;
  } catch (error) {
    console.error(`Error getting cookie ${key}:`, error);
    return null;
  }
};

// 获取用户信息
const getUserInfoFromStorage = (): UserInfo | null => {
  try {
    const storedUserInfo = localStorage.getItem('userInfo');
    if (!storedUserInfo || storedUserInfo === 'undefined' || storedUserInfo === 'null') {
      return null;
    }
    const parsedInfo = JSON.parse(storedUserInfo);
    // 检查解析后的对象是否有效
    if (!parsedInfo || typeof parsedInfo !== 'object') {
      return null;
    }
    return parsedInfo;
  } catch (error) {
    console.error('Error parsing user info:', error);
    return null;
  }
};

interface PrivateRouteProps {
  children: React.ReactNode;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const accessToken = getCookie('ACCESS_TOKEN');
  const userId = getCookie('userId');
  const userInfo = getUserInfoFromStorage();

  useEffect(() => {
    // 如果是登录页面，不执行检查
    if (location.pathname === '/login') {
      return;
    }

    console.log('PrivateRoute check:', {
      path: location.pathname,
      accessToken,
      userId,
      hasUserInfo: !!userInfo,
    });

    // 检查登录状态和用户信息
    if (!accessToken || !userId || !userInfo) {
      console.log('Redirecting to login. Reason:', {
        accessToken: !accessToken ? 'missing' : 'present',
        userId: !userId ? 'missing' : 'present',
        userInfo: !userInfo ? 'missing' : 'present',
      });

      // 保存当前路径到sessionStorage
      const path = location.pathname + location.search;
      if (path !== '/login') {
        sessionStorage.setItem('redirectPath', path);
      }
      // 使用 navigate 进行跳转
      navigate('/login', { replace: true });
    }
  }, [accessToken, userId, userInfo, location, navigate]);

  // 如果未登录，返回null以防止渲染受保护的内容
  if (!accessToken || !userId || !userInfo) {
    return null;
  }

  return <>{children}</>;
};

export default PrivateRoute;
